import React, { useEffect, useMemo, useState } from 'react';

import superagent from 'superagent';

import { backendUrl } from '../../../helpers/consts.js';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@shadcn/components/ui/tooltip';

// Long enough that sweeping the mouse down the table doesn't open (and fetch) every row.
const OPEN_DELAY_MS = 300;
// Kept clear of the window's edges.
const EDGE_GAP_PX = 8;
// Radix sets the available-* vars to the room left beside the trigger, so the card never
// runs off screen: it wraps to fit, and a long one scrolls (the card stays open while hovered).
// whiteSpace resets the nowrap it would otherwise inherit from the table cell it renders in.
const FIT_TO_WINDOW = {
  whiteSpace: 'normal',
  maxWidth: 'min(36rem, var(--radix-tooltip-content-available-width))',
  maxHeight: 'var(--radix-tooltip-content-available-height)',
  overflowY: 'auto',
  overflowWrap: 'anywhere',
};

const fmtAmount = (minor) =>
  (minor / 100).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (d) => (d ? new Date(d).toISOString().slice(0, 10) : '-');

// This SKU's share of its container's volume: the share of the container's fees it carries.
const cbmShare = (c) =>
  c.containerTotalCubic > 0 ? (c.unitCubic * c.qtyOnContainer) / c.containerTotalCubic : null;

/**
 * Spell out how a rate was made, each line as its formula then the same formula with the
 * real figures. Same wording as next-frontend `components/cogs/rate-breakdown.ts`.
 * @param {object} b - the `/cogs/rates/:sku/containers` response
 * @returns {{ rate: {calc: string, values: string}, containers: object[], warning: string | null }}
 */
const describeRateCalc = (b) => {
  const n = b.containers.length;
  // The market's days, as the rates table shows them; UTC dates only for an older rate.
  const window = `received ${b.windowFromDate || fmtDate(b.windowStart)} → ${b.windowToDate || fmtDate(b.windowEnd)}`;
  // Every container counts once, whatever its qty: the engine's straight average.
  const average = n ? Math.round(b.containers.reduce((s, c) => s + c.cogUnitMinorUnits, 0) / n) : null;
  const missing = b.missingContainerIds.length;
  // Counted containers but never recorded which (a rate written before it kept containerIds):
  // nothing to list or check, so only the rate is shown.
  const unrecorded = !!b.containerCount && !n && !missing;

  // Decided by the rate's own count, as the table's Containers column is, not by the rows found.
  const rate = !b.containerCount
    ? {
        calc: `No container landed in this window (${window})`,
        // 0 at no containers is a placeholder no container ever costed, as the backend reads it.
        values: b.rateMinorUnits ? `Last calculated rate: ${fmtAmount(b.rateMinorUnits)}` : 'No rate calculated yet',
      }
    : unrecorded
    ? {
        calc: `No container record for this rate (${window})`,
        values: `Last calculated rate: ${fmtAmount(b.rateMinorUnits)}`,
      }
    : {
        calc: `Rate = sum of each container's landed cost per unit ÷ ${b.containerCount} container${
          b.containerCount === 1 ? '' : 's'
        } (${window})`,
        values:
          average == null
            ? `= ${fmtAmount(b.rateMinorUnits)}`
            : `= (${b.containers.map((c) => fmtAmount(c.cogUnitMinorUnits)).join(' + ')}) ÷ ${n} = ${fmtAmount(average)}`,
      };

  const containers = b.containers.map((c) => {
    const share = cbmShare(c);
    return {
      heading: `${c.containerName ?? c.containerId} · received ${fmtDate(c.receivedAt)} · qty ${c.qtyOnContainer}`,
      landed: {
        calc: 'Landed = FOB + Freight',
        values: `= ${fmtAmount(c.fobMinorUnits)} + ${fmtAmount(c.freightPerUnitMinorUnits)} = ${fmtAmount(c.cogUnitMinorUnits)}`,
      },
      freight: {
        calc: 'Freight = Container fees × CBM share ÷ Qty',
        values:
          share == null
            ? 'Container has no recorded volume, so no fees were allocated'
            : `= ${fmtAmount(c.importFeesTotalMinorUnits)} × ${(share * 100).toFixed(2)}% ÷ ${c.qtyOnContainer} = ${fmtAmount(
                c.freightPerUnitMinorUnits,
              )}`,
      },
    };
  });

  const stored = fmtAmount(b.rateMinorUnits);
  let warning = null;
  if (missing) {
    warning = `${missing} container(s) this rate averaged no longer have cost rows, so the figures above won't reproduce the rate of ${stored}. Recalculate COGS to refresh it.`;
  } else if (average !== null && average !== b.rateMinorUnits) {
    warning = `These containers' costs changed after the rate of ${stored} was calculated. Recalculate COGS to update it.`;
  }
  return { rate, containers, warning };
};

const Calc = ({ line }) => (
  <div style={{ marginBottom: 4 }}>
    <div className="text-muted-foreground">{line.calc}</div>
    <div style={{ fontFamily: 'monospace' }}>{line.values}</div>
  </div>
);

/**
 * A SKU that, hovered, shows how its COGS rate was calculated. Fetched on first hover and
 * then kept until the rates table reloads (Recalculate COGS bumps `ratesVersion`) or the
 * market changes, so it never moves between the table's loads; fetched directly rather
 * than via useQuery, whose error handling navigates away.
 * @param {object} props
 * @param {string} props.sku
 * @param {string} props.region
 * @param {number} props.ratesVersion - the table's refresh count
 */
const RateBreakdownTooltip = ({ sku, region, ratesVersion }) => {
  const key = `${sku}|${region}|${ratesVersion}`;
  const [open, setOpen] = useState(false);
  // Tagged with the key it was loaded for, so another SKU's, market's or load's never shows.
  const [loaded, setLoaded] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!open || loaded?.key === key) return undefined;
    let cancelled = false;
    setFailed(false);
    superagent
      .get(`${backendUrl}/cogs/rates/${encodeURIComponent(sku)}/containers`)
      .query({ region })
      .set('Authorization', `Bearer ${localStorage.getItem('authJWT')}`)
      .set('accept', 'json')
      .then((response) => !cancelled && setLoaded({ key, data: response.body }))
      .catch(() => !cancelled && setFailed(true));
    return () => {
      cancelled = true;
    };
  }, [open, key, loaded?.key, sku, region]);

  const calc = useMemo(() => (loaded?.key === key ? describeRateCalc(loaded.data) : null), [loaded, key]);

  return (
    <TooltipProvider delayDuration={OPEN_DELAY_MS}>
      <Tooltip open={open} onOpenChange={setOpen}>
        <TooltipTrigger asChild>
          <span style={{ textDecoration: 'underline', cursor: 'help' }}>{sku}</span>
        </TooltipTrigger>
        <TooltipContent
          side="right"
          align="start"
          collisionPadding={EDGE_GAP_PX}
          style={{ ...FIT_TO_WINDOW, fontSize: 12 }}
        >
          {failed ? (
            <div className="text-danger">Could not load how this rate was calculated.</div>
          ) : !calc ? (
            <div className="text-muted-foreground">Loading…</div>
          ) : (
            <div style={{ padding: '4px 0' }}>
              <div className="font-weight-bold" style={{ marginBottom: 4 }}>
                {sku} · {region}
              </div>
              <Calc line={calc.rate} />
              {calc.containers.map((c) => (
                <div key={c.heading} style={{ borderTop: '1px solid #e5e5e5', marginTop: 8, paddingTop: 8 }}>
                  <div style={{ fontWeight: 500, marginBottom: 4 }}>{c.heading}</div>
                  <Calc line={c.landed} />
                  <Calc line={c.freight} />
                </div>
              ))}
              {calc.warning && (
                <div className="text-danger" style={{ borderTop: '1px solid #e5e5e5', marginTop: 8, paddingTop: 8 }}>
                  {calc.warning}
                </div>
              )}
            </div>
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default RateBreakdownTooltip;
